/**
 * ═══════════════════════════════════════════════════════════════════════════
 * QUÉ SE SIRVE EN CADA CAMINO
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Un solo origen —el puerto 8130— para las tres cosas. Servirlas en orígenes
 * distintos es la cicatriz nº 1 heredada, y no se repite.
 *
 *   /            la página de venta
 *   /app         la plataforma
 *   /confirmar   la plataforma, entrando desde un correo
 *   /recuperar   la plataforma, entrando desde un correo
 *   /panel/      el superadmin
 *   /api/        la API
 *
 * ───────────────────────────────────────────────────────────────────────────
 * POR QUÉ LA VENTA SE QUEDA CON LA RAÍZ
 *
 * Porque la raíz es lo que alguien teclea, lo que lee un buscador y adonde
 * apunta un anuncio. Quien llega por primera vez a voicecomfort.com no viene
 * a entrar en su cuenta: viene a enterarse de qué es esto.
 *
 * Y quien ya tiene cuenta pierde un clic —el botón de la cabecera—, que es el
 * precio barato de esta pareja. Al revés, el visitante nuevo aterrizaría en
 * una aplicación vacía sin saber qué le están ofreciendo.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * POR QUÉ /confirmar Y /recuperar NO CUELGAN DE /app
 *
 * Porque salen dentro de un correo, y ahí la longitud del enlace es producto:
 * `voicecomfort.com/confirmar?t=…` se lee de un vistazo y se copia a mano si
 * el cliente de correo rompe el enlace. `…/app/confirmar?t=…` no aporta nada
 * y estorba. Son dos caminos, no una jerarquía.
 * ═══════════════════════════════════════════════════════════════════════════
 */

/** La plataforma. */
export const CAMINO_DE_LA_APP = "/app";

/**
 * Los dos que salen dentro de un correo.
 *
 * Tienen nombre propio porque `correo/enlaces.ts` los escribía a mano —un
 * `tipo === "confirmar_correo" ? "confirmar" : "recuperar"` suelto— y así
 * renombrar el reparto dejaba los enlaces de todos los correos ya enviados
 * apuntando a la página de venta, sin error y sin que nadie se enterara.
 *
 * ── 15/09/2026 · YA NO SALEN EN NINGÚN CORREO NUEVO, Y SE QUEDAN ─────────────
 *
 * Se entra con un código («el login es por OTP») y `enlaces.ts` ya no existe.
 * Pero los correos que ya se mandaron siguen en los buzones con estos
 * enlaces, y el motivo de arriba vale entero: quitarlos de aquí haría que
 * aterrizaran en la página de venta. Así que se siguen sirviendo con la
 * plataforma, y `App.tsx` los manda a la pantalla de entrar.
 */
export const CAMINO_DE_CONFIRMAR = "/confirmar";
export const CAMINO_DE_RECUPERAR = "/recuperar";

/**
 * Los caminos que sirven LA PLATAFORMA en vez de la página de venta.
 *
 * Esta lista es la única verdad sobre eso: la sirve el servidor, la comprueba
 * el build contra los botones de la landing, y de sus constantes salen los
 * enlaces de los correos. Un «Empezar» no puede apuntar a un sitio que no
 * lleva a ninguna parte, y un enlace de confirmación tampoco.
 */
export const CAMINOS_DE_LA_APP = [
  CAMINO_DE_LA_APP,
  CAMINO_DE_CONFIRMAR,
  CAMINO_DE_RECUPERAR,
] as const;

/**
 * Adonde llevan los botones de la landing.
 *
 * CON BARRA FINAL, y no es cosmética. En producción da igual —este servidor
 * sirve `index.html` con barra y sin ella—, pero en desarrollo el servidor de
 * Vite tiene `base: "/app/"` y a `/app` responde «did you mean /app/?».
 *
 * O sea: sin la barra, el único sitio donde el embudo NO se puede caminar es
 * la máquina en la que se desarrolla, que es justo donde hay que caminarlo.
 */
export const CAMINO_DE_EMPEZAR = "/app/";

/**
 * Y EL «EMPEZAR» DE LA VENTA, QUE ABRE EL ONBOARDING DIRECTAMENTE (13/09).
 *
 * `CAMINO_DE_EMPEZAR` lo usan también los correos para «ir a mi plan», así que
 * no puede significar «empieza de cero». Éste solo lo pone la landing, y
 * `App.tsx` lo lee: sin plan abre el onboarding; con plan, va al plan.
 */
export const CAMINO_DE_EMPEZAR_DESDE_LA_VENTA = "/app/?empezar";

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * Y ADÓNDE VA QUIEN YA ES CLIENTE
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * La página de venta tenía UN botón, y decía «Empezar». A quien no conoce el
 * producto le sirve; a quien lleva tres meses practicando le suena a volver a
 * empezar — y era su única puerta, porque la pantalla de entrar no tenía
 * dirección: solo se llegaba pulsando «Entrar» dentro de la aplicación.
 *
 * O sea que el cliente que teclea voicecomfort.com tenía que pulsar «Empezar»,
 * aterrizar en la portada de la plataforma y buscar «Entrar» en la barra. Dos
 * clics y un momento de duda, todos los días, en un producto de uso diario.
 *
 * `?entrar` lo lee `App.tsx` y abre la pantalla de entrar (hasta el 15/09, la
 * de la contraseña; desde entonces pide el correo y manda un código) — y solo
 * si de verdad no hay sesión: a quien ya está dentro no se le enseña un
 * formulario. Es también adonde lleva el botón del correo «Ya puedes entrar». Es una decisión del dueño del 06/09/2026: *«REGISTRARSE ABRE EL
 * ONBOARDING, Y LOGIN ABRE LA PANTALLA DE LOGIN»*.
 *
 * Va aquí y no escrito a mano en la landing por lo mismo que los otros: dos
 * aplicaciones lejanas que tienen que llegar al mismo sitio.
 */
export const CAMINO_DE_ENTRAR = "/app/?entrar";

/**
 * EL BOTÓN DE PAGAR, y está aquí por la misma razón que los dos de arriba.
 *
 * Lo pulsan DOS aplicaciones distintas —la página de venta y la pantalla de la
 * cuenta— y las dos tienen que llegar al mismo sitio. Escrito a mano en cada
 * una, renombrar la ruta deja una de las dos pulsando un 404 sin que nada lo
 * cante: un botón de pagar roto no da error, no hace nada.
 *
 * Es el mismo motivo por el que `CAMINO_DE_CONFIRMAR` tiene nombre propio, y
 * el mismo caso: dos sitios lejanos escribiendo la misma cadena.
 *
 * ⚠ ESTO ES LA PUERTA, NO EL DESTINO. Adónde va la persona a pagar lo decide
 * `apps/api/src/pasarela.ts`, y es el único sitio que se toca el día que haya
 * pasarela (D31). Aquí solo está el nombre de la ruta que se pulsa.
 */
export const CAMINO_DE_PAGAR = "/api/suscripcion/pagar";

/* ── La trastienda ─────────────────────────────────────────────────────── */

/**
 * TODO lo que cuelgue de aquí exige haber entrado al panel.
 *
 * La guardia está en el despacho de `servidor.ts` y mira este prefijo, no una
 * lista de rutas. Así una ruta nueva del panel nace cerrada aunque quien la
 * escriba no se acuerde de cerrarla — que es lo que pasó con todas las que ya
 * había.
 */
export const PREFIJO_DEL_PANEL = "/api/panel";

/**
 * La única excepción, y por razones obvias: para entrar hace falta poder
 * llamar a la puerta sin haber entrado.
 *
 * Está aquí, junto al prefijo, para que se lean de una vez: una excepción a
 * una regla de seguridad escrita lejos de la regla es una excepción que nadie
 * vuelve a revisar.
 */
export const CAMINO_DE_ENTRAR_AL_PANEL = "/api/panel/entrar";

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * LA FORMA CANÓNICA DE UN CAMINO, Y POR QUÉ EXISTE ESTA FUNCIÓN
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * `GET /api//panel/usuarios` DEVOLVÍA 200 CON EL CORREO DE TODOS LOS CLIENTES.
 *
 * Con una barra de más. Sin cookie, sin contraseña, desde cualquier sitio de
 * internet. Y con `/api//panel/usuario/<id>` salían además las respuestas del
 * filtro de salud de esa persona, que es justo el dato que este producto
 * existe para no filtrar.
 *
 * ── POR QUÉ PASABA, QUE ES LO QUE HAY QUE ENTENDER ──────────────────────────
 *
 * Había DOS normalizaciones distintas del mismo camino, escritas en dos
 * ficheros que nunca se leen juntos:
 *
 *   · la guardia miraba el texto tal cual: `camino.startsWith("/api/panel")`.
 *     `"/api//panel/usuarios"` NO empieza por ahí, así que la guardia no
 *     saltaba.
 *   · el enrutador partía por barras y TIRABA LOS HUECOS
 *     (`split("/").filter(p => p.length > 0)`). Para él `/api//panel/usuarios`
 *     y `/api/panel/usuarios` son el mismo camino, así que lo resolvía y
 *     ejecutaba el manejador.
 *
 * Las dos piezas eran razonables por separado. El agujero estaba en el hueco
 * entre ellas: **quien decide si hace falta llave y quien abre la puerta no
 * estaban mirando la misma cerradura.**
 *
 * ── LA REGLA QUE LO CIERRA ──────────────────────────────────────────────────
 *
 * El camino se normaliza UNA VEZ, aquí, y ese mismo texto es el que ven la
 * guardia y el enrutador. No es que ahora coincidan: es que ya no puede haber
 * dos opiniones, porque solo se calcula una.
 *
 * `verificar:produccion` lo prueba por las dos caras: que un camino torcido
 * que el enrutador resuelve a una ruta del panel lo vea también la guardia.
 * Sin esa prueba esto es una costumbre, y las costumbres se pierden.
 */
export function caminoCanonico(camino: string): string {
  /*
    `new URL()` ya colapsa `.` y `..` antes de llegar aquí. Lo que NO colapsa
    son las barras vacías, y ésas son las que abrían el agujero.
  */
  const sinDobles = camino.replace(/\/{2,}/g, "/");
  /*
    Y la barra final se quita, menos en la raíz: `/api/panel/usuarios/` es el
    mismo sitio que sin ella, y el enrutador ya lo trataba así. Escribirlo aquí
    es que lo trate así TAMBIÉN la guardia.
  */
  if (sinDobles.length > 1 && sinDobles.endsWith("/")) return sinDobles.slice(0, -1);
  return sinDobles;
}

/**
 * Si esa petición tiene que pasar por la guardia del panel.
 *
 * ⚠ RECIBE EL CAMINO YA CANÓNICO. Llamarla con el crudo es exactamente el
 * fallo que `caminoCanonico` viene a cerrar, así que normaliza otra vez por su
 * cuenta: es barato, y así no depende de que quien la llame se acuerde.
 */
export function exigePanel(caminoCrudo: string): boolean {
  const camino = caminoCanonico(caminoCrudo);
  return camino.startsWith(PREFIJO_DEL_PANEL) && camino !== CAMINO_DE_ENTRAR_AL_PANEL;
}

/** Si ese camino lo sirve la plataforma. `/app/loquesea` también. */
export function esCaminoDeLaApp(camino: string): boolean {
  return CAMINOS_DE_LA_APP.some((c) => camino === c || camino.startsWith(`${c}/`));
}
